import { formatDate } from '@angular/common';
import { EventEmitter, Inject, Injectable, LOCALE_ID } from '@angular/core';
import { UserService } from '@app/core/auth';
import { ApiListResponse } from '@app/core/backend';
import { ApiClient } from '@eng-ds/api-client';
import { I18nService } from '@eng-ds/translate';
import { NbDateFnsDateService } from '@nebular/date-fns';
import { NbGlobalPhysicalPosition, NbToastrService } from '@nebular/theme';
import { subMonths } from 'date-fns';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
	Autolecture,
	Elaboration,
	ElaborationTableRow,
	KindOfFlow
} from '../models';

@Injectable()
export class UploadsService {
	public changeBadgeable: EventEmitter<boolean> = new EventEmitter<boolean>();

	constructor(
		@Inject(LOCALE_ID) public locale: string,
		private apiClient: ApiClient,
		private userService: UserService,
		private toastrService: NbToastrService,
		private i18n: I18nService,
		private dateService: NbDateFnsDateService
	) {}

	getFilterBeginDate() {
		return `${formatDate(
			subMonths(new Date(), 1),
			'yyyy-MM-dd',
			this.locale
		)}T00:00:00.000Z`;
	}

	getFilterEndDate() {
		return `${formatDate(new Date(), 'yyyy-MM-dd', this.locale)}T23:59:00.000Z`;
	}

	get(
		kindOfFlow?: KindOfFlow
	): Observable<ApiListResponse<ElaborationTableRow>> {
		const apiObs$ = this._getApiRequestByKindFlow(kindOfFlow).pipe(
			tap((resp) => {
				this._changeBadgeStatus(resp?.Body || []);
			}),
			map((resp) => {
				return {
					Header: {} as any,
					Body: this._mapElaborationToTableRow(resp.Body || [])
				};
			})
		);
		return apiObs$;
	}

	private _getApiRequestByKindFlow(kindOfFlow: KindOfFlow) {
		switch (kindOfFlow) {
			case KindOfFlow.REQUEST:
				return this.apiClient.request<ApiListResponse<Elaboration>>(
					'RIC_7a',
					this._getBodyRequestByFlow(kindOfFlow)
				);

			case KindOfFlow.AUTOLECTURE:
				return this.apiClient
					.request<
						ApiListResponse<Autolecture>
					>('AUTOL_3', this._getBodyRequestByFlow(kindOfFlow))
					.pipe(
						map((resp) => ({
							Header: {} as any,
							Body: this._mapAutolecturesToElaborations(
								resp?.Body || []
							)
						}))
					);

			default:
				return this.apiClient.request<ApiListResponse<Elaboration>>(
					'RIC_7a',
					this._getBodyRequestByFlow(kindOfFlow)
				);
		}
	}

	private _changeBadgeStatus(elaborations: Elaboration[]) {
		this.changeBadgeable.emit(
			!!elaborations.find(
				(elab) => elab.StatoElaborazione === 'IN ELABORAZIONE'
			)
		);
	}

	private _mapAutolecturesToElaborations(autoLectures: Autolecture[]) {
		const elaborations: Elaboration[] = [];
		autoLectures.forEach((auto) =>
			elaborations.push({
				CodiceFlusso: auto.TipoFlusso,
				NomeFile: auto.NomeFile,
				DataInserimento: auto.DataInserimento,
				StatoElaborazione: auto.StatoElaborazione
			})
		);
		return elaborations;
	}

	private _mapElaborationToTableRow(elaborations: Elaboration[]) {
		const rows: ElaborationTableRow[] = [];
		elaborations.forEach((elab) => {
			const codServizio = elab.CodiceServizio;
			if (codServizio) {
				rows.push({
					...elab,
					CodServizio_CodFlusso: `${elab.CodiceServizio}_${elab.CodiceFlusso}`
				});
			} else {
				rows.push({
					...elab,
					CodServizio_CodFlusso: `${elab.CodiceFlusso}`
				});
			}
		});
		return rows;
	}

	private _getBodyRequestByFlow(kindOfFlow: KindOfFlow) {
		switch (kindOfFlow) {
			case KindOfFlow.REQUEST:
				return {
					Blocco: 0,
					AmbitoPortale: 'CARICAMENTO_RICHIESTE',
					DataInserimentoDa: this.getFilterBeginDate(),
					...this.getSpecIdVendita()
				};
			case KindOfFlow.AUTOLECTURE:
				return {
					DataCaricamentoDa: this.getFilterBeginDate(),
					DataCaricamentoA: this.getFilterEndDate(),
					...this.getSpecIdUDD()
				};
			default:
				return {
					Blocco: 0,
					DataInserimentoDa: this.getFilterBeginDate(),
					...this.getSpecIdVendita()
				};
		}
	}

	getSpecIdVendita() {
		if (this.userService.isSuperRole) {
			return { IdVendita: this.userService.user.PartitaIvaUdD };
		} else {
			return {};
		}
	}

	getSpecIdUDD() {
		if (this.userService.isSuperRole) {
			return { IdUDD: this.userService.user.PartitaIvaUdD };
		} else {
			return {};
		}
	}

	showError() {
		this.toastrService.show(
			this.i18n.translate('UTILS.TOASTR.GENERIC_ERROR.MESSAGE'),
			this.i18n.translate('UTILS.TOASTR.GENERIC_ERROR.TITLE'),
			{
				position: NbGlobalPhysicalPosition.TOP_RIGHT,
				status: 'danger',
				icon: 'close-outline'
			}
		);
	}
}
